import PageMeta from "@/components/PageMeta";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpDown } from "lucide-react";
import { fetchProducts, fetchCollection } from "@/lib/api";
import { CANVAS_COLLECTIONS, classifyCanvasAll } from "@/lib/canvasCollections";
import { getRouteState } from "@/lib/navigation";
import ProductCard from "@/components/ProductCard";
import SectionFadeIn from "@/components/SectionFadeIn";

function isCanvasProduct(product) {
  const pt = (product.productType || product.product_type || "").toLowerCase();
  return pt.includes("canvas") && !pt.includes("mural");
}

const SORT_OPTIONS = [
  { id: "default", label: "Featured" },
  { id: "az", label: "A \u2013 Z" },
  { id: "za", label: "Z \u2013 A" },
  { id: "price-low", label: "Price: Low to High" },
  { id: "price-high", label: "Price: High to Low" },
];

function getMinPrice(product) {
  const variants = product.variants || [];
  const prices = variants
    .map((v) => parseFloat(v.price?.amount || "0"))
    .filter((p) => p > 0);
  return prices.length > 0 ? Math.min(...prices) : 999999;
}

export default function CanvasCollectionPage() {
  const { slug, category } = useParams();
  const collectionSlug = category || slug;
  const location = useLocation();
  const navigate = useNavigate();
  const [allCanvas, setAllCanvas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("default");

  const collection = useMemo(
    () => CANVAS_COLLECTIONS.find((c) => c.slug === collectionSlug),
    [collectionSlug]
  );

  const otherCollections = useMemo(
    () => CANVAS_COLLECTIONS.filter((c) => c.slug !== collectionSlug).slice(0, 4),
    [collectionSlug]
  );

  useEffect(() => {
    let active = true;
    setLoading(true);

    async function load() {
      try {
        const colData = await fetchCollection("canvas-prints");
        const products = (colData.collection?.products || []).filter(isCanvasProduct);
        if (products.length > 0) {
          if (active) setAllCanvas(products);
          return;
        }
      } catch (err) {
        console.error("CanvasCollectionPage: collection fetch failed", err);
      }

      try {
        const data = await fetchProducts({ first: 250 });
        const products = (data.products || []).filter(isCanvasProduct);
        if (active) setAllCanvas(products);
      } catch (err) {
        console.error("[CanvasCollectionPage] fetch failed:", err);
        if (active) setAllCanvas([]);
      }
    }

    load().finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const matchedProducts = useMemo(() => {
    if (!collection) return [];
    return allCanvas.filter((p) => {
      const slugs = classifyCanvasAll(p);
      return slugs.includes(collection.slug);
    });
  }, [allCanvas, collection]);

  const sorted = useMemo(() => {
    const list = [...matchedProducts];
    switch (sortBy) {
      case "az":
        return list.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
      case "za":
        return list.sort((a, b) => (b.title || "").localeCompare(a.title || ""));
      case "price-low":
        return list.sort((a, b) => getMinPrice(a) - getMinPrice(b));
      case "price-high":
        return list.sort((a, b) => getMinPrice(b) - getMinPrice(a));
      default:
        return list.sort((a, b) => {
          const aImg = a.images?.length > 0 ? 0 : 1;
          const bImg = b.images?.length > 0 ? 0 : 1;
          return aImg - bImg;
        });
    }
  }, [matchedProducts, sortBy]);

  if (!collection) {
    return (
      <div className="py-24 text-center max-w-7xl mx-auto px-6">
        <h1 className="text-2xl text-brand-text">Collection not found</h1>
        <Link to="/canvas-category-landing" className="mt-4 inline-block text-brand-gold hover:underline">
          Back to Canvas Prints
        </Link>
      </div>
    );
  }

  const Icon = collection.icon;

  return (
    <div className="py-16 md:py-24" data-testid="canvas-collection-page">
      <PageMeta
        title={`${collection.title} Canvas Prints`}
        description={collection.description}
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">

        {/* Back link */}
        <Link
          to="/canvas-category-landing"
          state={getRouteState(location)}
          className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.12em] text-brand-text-secondary/60 hover:text-brand-gold transition-colors duration-300 mb-8"
          data-testid="back-to-canvas"
        >
          <ArrowLeft size={14} /> Canvas Prints
        </Link>

        {/* Hero */}
        <SectionFadeIn>
          <div
            className="relative rounded-sm overflow-hidden border border-white/[0.06]"
            data-testid="canvas-collection-hero"
          >
            <div className="aspect-[4/3] md:aspect-[21/7]">
              <img
                src={collection.image}
                alt={collection.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#0A0D14]/90 via-[#0A0D14]/60 to-[#0A0D14]/20" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0D14]/70 to-transparent" />
            </div>
            <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-10">
              <div className="flex items-center gap-2.5 text-brand-gold mb-3">
                <Icon size={20} />
                <span className="text-[11px] uppercase tracking-[0.2em] font-semibold">
                  Canvas Collection
                </span>
              </div>
              <h1
                className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-brand-text max-w-xl"
                data-testid="canvas-collection-title"
              >
                {collection.title}
              </h1>
              <p className="mt-3 text-[14px] text-brand-text-secondary/80 max-w-lg leading-relaxed">
                {collection.description}
              </p>
            </div>
          </div>
        </SectionFadeIn>

        {/* ── Browse chips + sort controls ── */}
        <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="canvas-browse-controls">
          {/* Collection chips */}
          <div className="flex flex-wrap gap-2 overflow-x-auto pb-1" data-testid="canvas-browse-chips">
            <Link
              to="/canvas-prints"
              state={getRouteState(location)}
              className="rounded-full border border-white/10 px-4 py-2 text-[12px] font-medium text-brand-text-secondary/60 hover:text-brand-text hover:border-white/25 transition-colors shrink-0"
              data-testid="chip-all"
            >
              All Canvas
            </Link>
            {CANVAS_COLLECTIONS.map((col) => (
              <button
                key={col.slug}
                type="button"
                onClick={() => navigate(`/canvas-prints/${col.slug}`, { state: getRouteState(location) })}
                className={`rounded-full px-4 py-2 text-[12px] font-medium transition-all shrink-0 ${
                  collectionSlug === col.slug
                    ? "bg-brand-gold text-brand-bg"
                    : "border border-white/10 text-brand-text-secondary/60 hover:text-brand-text hover:border-white/25"
                }`}
                data-testid={`chip-${col.slug}`}
              >
                {col.title}
              </button>
            ))}
          </div>

          {/* Sort control */}
          <div className="flex items-center gap-2 shrink-0" data-testid="canvas-sort-control">
            <ArrowUpDown size={14} className="text-brand-text-secondary/40" />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-transparent border border-white/10 rounded-sm text-[12px] text-brand-text-secondary/70 px-3 py-2 outline-none focus:border-brand-gold cursor-pointer"
              data-testid="canvas-sort-select"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id} className="bg-brand-bg text-brand-text">
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Products grid */}
        <section className="mt-8" data-testid="canvas-collection-products">
          {loading ? (
            <div className="py-16 text-center">
              <div className="inline-block w-6 h-6 border-2 border-brand-gold/30 border-t-brand-gold rounded-full animate-spin" />
              <p className="mt-3 text-[13px] text-brand-text-secondary/40">Loading designs...</p>
            </div>
          ) : sorted.length > 0 ? (
            <>
              <div className="flex items-end justify-between mb-6">
                <p className="text-[13px] text-brand-text-secondary/60">
                  {sorted.length} design{sorted.length !== 1 && "s"} in this collection
                </p>
                <Link
                  to="/canvas-prints"
                  state={getRouteState(location)}
                  className="text-[12px] uppercase tracking-[0.1em] text-brand-text-secondary/50 hover:text-brand-gold transition-colors"
                >
                  View all canvas
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {sorted.map((product, i) => (
                  <SectionFadeIn key={product.id} delay={i * 60}>
                    <ProductCard
                      product={product}
                      variant="canvas"
                      basePath="/canvas-prints"
                    />
                  </SectionFadeIn>
                ))}
              </div>
            </>
          ) : (
            <div
              className="rounded-sm border border-white/[0.06] bg-white/[0.02] py-16 px-6 text-center"
              data-testid="canvas-collection-empty"
            >
              <Icon size={36} strokeWidth={1} className="mx-auto text-brand-gold/30 mb-4" />
              <h3 className="text-[18px] font-medium text-brand-text mb-2">
                {collection.title} canvas prints coming soon
              </h3>
              <p className="text-[13px] text-brand-text-secondary/60 max-w-md mx-auto leading-relaxed">
                Don't see what you're looking for? Send us a reference image and we'll print it as a custom canvas.
              </p>
              <Link
                to="/request-quote"
                state={getRouteState(location)}
                className="inline-flex items-center gap-2 mt-6 bg-brand-gold text-brand-bg hover:bg-brand-gold-hover transition-colors duration-300 rounded-sm px-7 py-3.5 font-medium text-[13px] tracking-wide"
                data-testid="canvas-collection-empty-cta"
              >
                Request a Custom Print <ArrowRight size={15} />
              </Link>
            </div>
          )}
        </section>

        {/* Custom print CTA */}
        <SectionFadeIn>
          <section
            className="mt-14 rounded-sm border border-brand-gold/15 bg-[linear-gradient(135deg,rgba(203,160,82,0.04),rgba(10,13,20,0.97))] px-6 py-10 md:px-10"
            data-testid="canvas-collection-custom-cta"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
              <div>
                <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-brand-gold mb-1">
                  Your image, our canvas
                </p>
                <h3 className="text-[20px] font-heading tracking-tight text-brand-text">
                  Want something custom?
                </h3>
                <p className="mt-1 text-[13px] text-brand-text-secondary/60">
                  Send us any high-res image and we'll print it on premium stretched canvas, any size.
                </p>
              </div>
              <Link
                to="/request-quote"
                state={getRouteState(location)}
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-bg hover:bg-brand-gold-hover transition-colors duration-300 rounded-sm px-8 py-3.5 font-semibold text-[13px] tracking-wide shrink-0"
                data-testid="canvas-collection-quote-cta"
              >
                Request Custom Print <ArrowRight size={15} />
              </Link>
            </div>
          </section>
        </SectionFadeIn>

        {/* Explore more */}
        <SectionFadeIn>
          <section className="mt-16" data-testid="canvas-explore-more">
            <p className="text-[11px] tracking-[0.22em] uppercase font-semibold text-brand-gold mb-6">
              Explore more styles
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {otherCollections.map((col) => {
                const ColIcon = col.icon;
                return (
                  <Link
                    key={col.slug}
                    to={`/canvas-prints/${col.slug}`}
                    state={getRouteState(location)}
                    className="group rounded-sm border border-white/[0.06] bg-white/[0.02] p-4 hover:border-brand-gold/30 transition-all duration-300"
                    data-testid={`canvas-explore-${col.slug}`}
                  >
                    <ColIcon
                      size={18}
                      className="text-brand-gold/60 group-hover:text-brand-gold transition-colors duration-300"
                    />
                    <p className="mt-2 text-[13px] font-medium text-brand-text group-hover:text-brand-gold transition-colors duration-300">
                      {col.title}
                    </p>
                    <p className="mt-0.5 text-[11px] text-brand-text-secondary/40 line-clamp-1">
                      {col.short}
                    </p>
                  </Link>
                );
              })}
            </div>
          </section>
        </SectionFadeIn>
      </div>
    </div>
  );
}
